import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";

import { cn } from "../utils/cn";
import { Label } from "./Label";

export type InputSize = "md" | "lg";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  error?: ReactNode;
  helper?: ReactNode;
  required?: boolean;
  inputSize?: InputSize;
}

const BASE =
  "w-full rounded-md border bg-neutral-0 text-sm text-text-primary outline-none transition-shadow " +
  "focus-visible:ring-2 focus-visible:ring-primary-500/40 " +
  "disabled:bg-neutral-50 disabled:text-text-disabled";

const SIZES: Record<InputSize, string> = {
  md: "h-10 px-4",
  lg: "h-12 px-4",
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, icon, error, helper, required, inputSize = "md", className, id, ...rest },
  ref,
): ReactElement {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = `${inputId}-error`;
  const helperId = `${inputId}-helper`;
  const describedBy = error ? errorId : helper ? helperId : undefined;

  return (
    <div className="w-full">
      {label && (
        <Label htmlFor={inputId} required={required} hint={hint}>
          {label}
        </Label>
      )}
      <div className="relative">
        {icon && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
          >
            {icon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={describedBy}
          className={cn(
            BASE,
            SIZES[inputSize],
            icon && "pl-10",
            error ? "border-danger-500 focus-visible:ring-danger-500/30" : "border-neutral-300",
            className,
          )}
          {...rest}
        />
      </div>
      {error ? (
        <p id={errorId} className="mt-2 text-xs text-danger-600">
          {error}
        </p>
      ) : helper ? (
        <p id={helperId} className="mt-2 text-xs text-text-tertiary">
          {helper}
        </p>
      ) : null}
    </div>
  );
});
